import { ref, computed } from "vue";
import { storeToRefs } from "pinia";
import { useCotizacionEstandarStore } from "@/store/cotizaciones/estandar/";
import appApi from "@/services/api";
import useClientes from "@/composables/useClientes";
import useUsuario from "@/composables/useUsuario";
import useArrendamientoTipo from "@/composables/useArrendamientoTipo";
import { exceptionRequestMsg } from "@/assets/js/util";
import useUtil from "@/composables/useUtil";
import useCotizacionEstandarKeyMetricsStore from "@/store/cotizaciones/estandar/key-metrics";
import useCotizacionEstandarLeaseParametersStore from "@/store/cotizaciones/estandar/lease-parameters";
import useCotizacionEstandarSourcesStore from "@/store/cotizaciones/estandar/sources";
import useCotizacionEstandarDatosStore from "@/store/cotizaciones/estandar/datos";

export const useCotizacionEstandar = () => {
  const { tablaTIR, clientes, usuarioLoggeado } = storeToRefs(
    useCotizacionEstandarStore()
  );
  const { getClientes } = useClientes();
  const { getUsuarioLoggeado } = useUsuario();
  const { get: getArrendamientosTipo } = useArrendamientoTipo();
  const arrendamientosTipo = ref([]);

  const getTablaTIR = async () => {
    try {
      const resp = await appApi().get(`tablaTIR`);
      const listaClientes = await getClientes();
      const usuario = await getUsuarioLoggeado();
      arrendamientosTipo.value = await getArrendamientosTipo();

      usuarioLoggeado.value = usuario;
      clientes.value = listaClientes;
      tablaTIR.value = resp.data;
    } catch (error) {
      console.error(error);
      exceptionRequestMsg(error);
    }
  };

  return {
    arrendamientosTipo,
    getTablaTIR,
  };
};

/* export const useCotizacionEstandarCalculos = () => {
  const {
    stateCalculosLeaseParameters: state,
    stateCalculosLeaseParameters,
    leaseParameters,
  } = storeToRefs(useCotizacionEstandarStore());
  const {
    obtenerDatoPorPlazo,
    precioFinal,
    calculaEnganche,
    calculaPmt,
    tdcInformativo,
  } = useCotizacionEstandar();
  const { stateCotizacion, tablaTIR } = storeToRefs(
    useCotizacionEstandarStore()
  );

  const keyMetrics = computed(() => {
    const facilityDrawEquip = ref(undefined); // TODO: Pendiente
    const facilityDrawEquipVAT = ref(undefined); // TODO: Pendiente
    const irrGross = ref(undefined); // TODO: Pendiente (=-D55+E60+E70+E65)
    const leasePrincipalAmount = ref(
      leaseParameters.value.equipmentValueBeforeDes.value -
        leaseParameters.value.downPaymentMonto.value
    );

    const monthlyPayment = ref(
      -calculaPmt(
        leaseParameters.value.coupon.value / 12 / 100,
        leaseParameters.value.duration.value -
          stateCalculosLeaseParameters.value.gracePeriodMonths,
        leasePrincipalAmount.value,
        -leaseParameters.value.rvMonto.value
      )
    );

    return {
      leasePrincipalAmount,
      monthlyPayment,
    };
  });

  

  

  const uses = computed(() => {
    const conceptos = {
      paymentToOEM: leaseParameters.value.equipmentValueBeforeNet.value,
      vatOnEquipment: leaseParameters.value.vatMonto.value,
      servicerFeeOpening: leaseParameters.value.openingFeeMonto.value,
      servicerFeeOpeningVat: sources.value.servicerFees.vatOpeningFee.total,
      firstMonthsRent: sources.value.firstMonthsRent.firstMonthsRent.total,
      vatOnOpeningFee: sources.value.firstMonthsRent.vatOnOpeningFee.total,
      partialDayInterest: sources.value.partialDayInterest.partialDayRent.total,
      partialDayInterestVat:
        sources.value.partialDayInterest.partialDayVAT.total,
    };

    const totalConceptos = computed(
      () =>
        conceptos.servicerFeeOpening +
        conceptos.servicerFeeOpeningVat +
        conceptos.firstMonthsRent +
        conceptos.vatOnOpeningFee +
        conceptos.partialDayInterest +
        conceptos.partialDayInterestVat
    );

    const totales = {
      equipment: conceptos.paymentToOEM + conceptos.vatOnEquipment,
      totalFeesPaidToServicer: totalConceptos,
      totalUses:
        conceptos.paymentToOEM +
        conceptos.vatOnEquipment +
        totalConceptos.value,
    };

    return {
      conceptos,
      totales,
    };
  });

  return {
    leaseParameters,
    sources,
    keyMetrics,
    total,
    totalSources,
    uses,
  };
}; */

export const useCotizacionEstandarCalculosTabla = () => {
  const { netVat, total: totalSources } = storeToRefs(
    useCotizacionEstandarSourcesStore()
  );
  const { stateCalculosLeaseParameters, state, stateCotizacion } = storeToRefs(
    useCotizacionEstandarStore()
  );

  const { keyMetrics } = storeToRefs(useCotizacionEstandarKeyMetricsStore());
  const { leaseParameters } = storeToRefs(
    useCotizacionEstandarLeaseParametersStore()
  );

  // --- parámetros reactivos (computed) ----------------------------------------
  const avance = computed(() => leaseParameters.value.rentUpFront.value); // D15 (número de mes con “-”)

  const periodoGraciaFin = computed(
    () => stateCalculosLeaseParameters.value.gracePeriodMonths
  ); // D45

  const tasa = computed(() =>
    Number(leaseParameters.value.couponPorcentaje.value)
  ); // C22

  const duracion = computed(() => Number(stateCotizacion.value.plazo)); // D23

  const principal = computed(() =>
    Number(keyMetrics.value.leasePrincipalAmount.value)
  ); // D7

  const residual = computed(() => Number(leaseParameters.value.rvMonto.value)); // D21

  const rentaGracia = computed(
    () => leaseParameters.value.monthlyInterestDuringGracePeriod.value
  ); // D41

  const fechaInicioPago = computed(() => state.value.fechaPrimerVencimiento);

  // totales sin gracia
  const nper = computed(() => duracion.value - periodoGraciaFin.value);

  // --- funciones de fecha -----------------------------------------------------
  function edate(base, offset, onlyYear = false) {
    const d = new Date(base);
    d.setMonth(d.getMonth() + offset);
    return onlyYear ? d.getFullYear() : d.toLocaleDateString("es-MX");
  }

  // --- PMT, IPMT, PPMT reactivos ---------------------------------------------
  function PMT() {
    if (tasa.value === 0)
      return (principal.value - residual.value) / duracion.value;

    const pow = Math.pow(1 + tasa.value, duracion.value);
    return (tasa.value * (principal.value * pow - residual.value)) / (pow - 1);
  }

  function IPMT(mes) {
    if (mes <= periodoGraciaFin.value || mes > duracion.value) return 0;

    const per = mes - periodoGraciaFin.value;
    const pmt = PMT();
    const balancePrev =
      principal.value * Math.pow(1 + tasa.value, per - 1) -
      (pmt * (Math.pow(1 + tasa.value, per - 1) - 1)) / tasa.value;

    return balancePrev * tasa.value;
  }

  function PPMT(mes) {
    if (mes <= periodoGraciaFin.value || mes > duracion.value) return 0;
    return PMT() - IPMT(mes);
  }

  // --- helpers adicionales ----------------------------------------------------
  function PMTtotal() {
    return PMT(); // pago fijo, sin argumentos
  }

  function interesGracia(mes) {
    return mes <= periodoGraciaFin.value ? rentaGracia.value : 0;
  }

  function RESVAL(mes) {
    return mes === duracion.value ? residual.value.toFixed(2) : 0;
  }

  function CF(mes) {
    if (mes !== duracion.value) return "-";
    return parseFloat(PMT(mes) + RESVAL(mes)).toFixed(2);
    //return interesGracia(mes);
  }

  const VATTOBECOLLECTED = (pmt, residualVal) => {
    return (
      (stateCalculosLeaseParameters.value.vat / 100) *
      (pmt + parseFloat(residualVal))
    ).toFixed(2);
  };

  const VATTOBEKEPT = (
    vatPrepaidEndPrevious,
    vatPrepaidBegin,
    vatToBeCollected
  ) => {
    console.log(
      parseFloat(vatPrepaidEndPrevious),
      parseFloat(vatPrepaidBegin),
      parseFloat(vatToBeCollected)
    );
    if (vatPrepaidEndPrevious == 0) return 0;

    if (vatToBeCollected != 0 && vatPrepaidBegin < vatToBeCollected)
      return vatToBeCollected;

    return vatPrepaidBegin;
  };

  const VATREFUND = (month, vatPrepaidBegin, vatToBeCollected) => {
    if (leaseParameters.value.vatRecoupedAtMonth == month)
      return vatPrepaidBegin - vatToBeCollected;

    return 0;
  };

  const VATPREPAIDEND = (vatPrepaidBegin, netVat) => {
    return vatPrepaidBegin - netVat;
  };

  const tabla = computed(() => {
    const rows = [];

    const fechaInicio = new Date(fechaInicioPago.value);

    // ---------- Fila 0 ----------
    let vatPrepaidBegin = 0;
    let vatPrepaidEnd = 121.001;

    const cfTotal = ref(
      Math.abs(
        -totalSources.value.equipment.facilityDraw +
          totalSources.value.equipmentVAT.lesseeContri +
          totalSources.value.firstMonthsRent.lesseeContri +
          totalSources.value.partialDayInterest.lesseeContri
      )
    );

    rows.push({
      year: fechaInicio.getFullYear(),
      date: fechaInicio.toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "short",
        year: "2-digit",
      }),
      month: 0,
      ipmt: "",
      ppmt: "",
      pmt: "",
      residual: "",
      cf: cfTotal.value,
      vatPrepaidBegin: "",
      vatToBeCollected: "",
      vatToBeKept: "",
      vatRefund: "",
      netVat: "",
      vatPrepaidEnd: netVat.value,
    });

    // ---------- Filas 1 … n ----------
    for (let i = 1; i <= duracion.value; i++) {
      const ipmt = IPMT(i);
      const ppmt = PPMT(i);
      const pmt = PMT();
      const residual = RESVAL(i);
      const cf = CF(i);

      vatPrepaidBegin = rows[i - 1].vatPrepaidEnd ?? "-";

      rows.push({
        year: edate(fechaInicioPago.value, i - 1, true),
        date: edate(fechaInicioPago.value, i - 1),
        month: i,
        ipmt,
        ppmt,
        pmt,
        residual,
        cf,
        vatPrepaidBegin,
        vatToBeCollected: VATTOBECOLLECTED(pmt, residual),
        vatToBeKept: VATTOBEKEPT(
          rows[i - 1].vatPrepaidEnd,
          vatPrepaidBegin,
          VATTOBECOLLECTED(pmt, residual)
        ),
        vatRefund: VATREFUND(vatPrepaidBegin, VATTOBECOLLECTED(pmt, residual)),
        netVat:
          VATREFUND(vatPrepaidBegin, VATTOBECOLLECTED(pmt, residual)) +
          VATTOBEKEPT(
            rows[i - 1].vatPrepaidEnd,
            vatPrepaidBegin,
            VATTOBECOLLECTED(pmt, residual)
          ),
        vatPrepaidEnd: VATPREPAIDEND(
          vatPrepaidBegin,
          VATREFUND(vatPrepaidBegin, VATTOBECOLLECTED(pmt, residual)) +
            VATTOBEKEPT(
              rows[i - 1].vatPrepaidEnd,
              vatPrepaidBegin,
              VATTOBECOLLECTED(pmt, residual)
            )
        ),
      });

      /* console.log(
        `Mes: ${i - 1}`,
        `Begin: ${rows[i - 1].vatPrepaidBegin}`,
        `End: ${rows[i - 1].vatPrepaidEnd}`
      ); */
      //console.log(vatPrepaidBegin, VATTOBECOLLECTED(pmt, residual), i);
    }

    return rows;
  });

  //console.clear();
  //console.log(tabla.value);

  return {
    edate,
    IPMT,
    PPMT,
    PMT,
    RESVAL,
    CF,
    avance,
    fechaInicioPago,
    tabla,
    netVat,
  };
};

export const useCotizacionEstandarFormato = () => {
  const { formatoMoneda } = useUtil();
  const {
    calculaValorResidualCalculo,
    calculaRentaMensual,
    calculaComision,
    calculaRentaEnDeposito,
    obtenerDatoPorPlazo,
  } = useCotizacionEstandarDatosStore();
  const { precioFinal } = storeToRefs(useCotizacionEstandarDatosStore());
  const { stateCotizacion } = storeToRefs(useCotizacionEstandarStore());

  const totaPagoInicial = (plazo) => {
    return formatoMoneda(
      stateCotizacion.value[`enganche_${plazo}`] +
        calculaComision(plazo) +
        calculaRentaEnDeposito(plazo)
    );
  };

  const valorResidual = (plazo) => {
    return formatoMoneda(
      precioFinal.value * (obtenerDatoPorPlazo(plazo, "residual_cliente") / 100)
    );
  };

  const totalRentas = (plazo) => {
    return calculaRentaMensual(plazo) * plazo - calculaRentaMensual(plazo);
  };

  const totalGastosIniciales = computed(() => {
    return (
      stateCotizacion.value[`enganche_${stateCotizacion.value.plazo}`] +
        calculaComision(stateCotizacion.value.plazo) +
        calculaRentaMensual(stateCotizacion.value.plazo) +
        stateCotizacion?.value.seguro ??
      0 + stateCotizacion?.value.gastosLegales ??
      0 + stateCotizacion?.value.gastosImporacion ??
      0
    );
  });

  const totalEgresos = computed(() => {
    return (
      totalGastosIniciales.value +
      calculaValorResidualCalculo(stateCotizacion.value.plazo) +
      totalRentas(stateCotizacion.value.plazo)
    );
  });

  const totalEgresosMasDeducible = computed(() => {
    return (
      (totalGastosIniciales.value + totalRentas(stateCotizacion.value.plazo)) *
        -0.3 +
      totalEgresos.value
    );
  });

  const costoFinancieroAnualConBeneficio = computed(() => {
    return (
      ((totalEgresosMasDeducible.value - precioFinal.value) /
        precioFinal.value /
        stateCotizacion.value.plazo) *
      12
    );
  });

  const costoFinancieroAnualSinBeneficio = computed(() => {
    return (
      ((totalEgresos.value - precioFinal.value) /
        precioFinal.value /
        stateCotizacion.value.plazo) *
      12
    );
  });

  const mostrarPlazos = ref({
    12: true,
    24: true,
    36: true,
    48: true,
    60: true,
  });

  const plazos = [12, 24, 36, 48, 60];

  return {
    costoFinancieroAnualConBeneficio,
    costoFinancieroAnualSinBeneficio,
    mostrarPlazos,
    plazos,
    totalEgresos,
    totalGastosIniciales,
    totalEgresosMasDeducible,
    totalRentas,
    totaPagoInicial,
    valorResidual,
  };
};
